import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const FIELDS = [["price_inr", "₹ price", 1], ["price_usd", "$ price", 0.01], ["credits", "Generations", 1], ["days", "Days", 1]];

const PlanRow = ({ plan, onSaved }) => {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  const dirty = FIELDS.some(([k]) => String(form[k]) !== String(plan[k])) || form.active !== plan.active;
  const save = async () => {
    setSaving(true);
    try {
      const body = { price_inr: Number(form.price_inr), price_usd: Number(form.price_usd), credits: Number(form.credits), days: Number(form.days), active: form.active };
      const { data } = await api.put(`/admin/plans/${plan.plan_id}`, body);
      onSaved(data);
      toast.success(`${plan.name} saved. New prices apply to new purchases only.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr className="border-t border-border" data-testid={`admin-plan-${plan.plan_id}`}>
      <td className="py-2 font-semibold">{plan.name}{plan.badge && <span className="ml-2 text-[10px] text-accent uppercase">{plan.badge}</span>}</td>
      {FIELDS.map(([k, , step]) => (
        <td key={k}><input type="number" step={step} min={step} value={form[k]} data-testid={`admin-plan-${plan.plan_id}-${k}`}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-24 h-9 rounded-lg bg-secondary px-2 text-sm" /></td>
      ))}
      <td><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid={`admin-plan-${plan.plan_id}-active`} /></td>
      <td className="text-right">
        <button onClick={save} disabled={!dirty || saving} data-testid={`admin-plan-${plan.plan_id}-save`}
          className="text-xs font-semibold rounded-full px-3 py-1.5 brand-gradient text-white disabled:opacity-30">{saving ? "Saving…" : "Save"}</button>
      </td>
    </tr>
  );
};

export const AdminPlans = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/plans").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const toggleIntl = async (v) => {
    if (v && !window.confirm("Only turn this on after Razorpay has approved International Payments for your live account. Continue?")) return;
    try {
      const { data: d } = await api.put("/admin/settings/payments", { international_enabled: v });
      setData((x) => ({ ...x, ...d }));
      toast.success(v ? "International card payments are ON." : "International card payments are OFF.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!data) return <section className="panel p-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></section>;
  return (
    <section className="panel p-6 space-y-5" data-testid="admin-plans">
      <h2 className="text-lg font-semibold">Plans & pricing</h2>
      <label className="flex items-center gap-3 text-sm rounded-xl border border-border p-4">
        <Switch checked={data.international_enabled} onCheckedChange={toggleIntl} data-testid="admin-intl-toggle" />
        <span><b>International card payments (USD)</b><br /><span className="text-xs text-muted-foreground">Turn on only after Razorpay approves International Payments on your live account. While off, customers outside India see USD prices with "Coming soon".</span></span>
      </label>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-2">Plan</th>{FIELDS.map(([k, l]) => <th key={k}>{l}</th>)}<th>On sale</th><th /></tr></thead>
          <tbody>
            {data.plans.map((p) => (
              <PlanRow key={p.plan_id} plan={p} onSaved={(np) => setData((x) => ({ ...x, plans: x.plans.map((q) => (q.plan_id === np.plan_id ? np : q)) }))} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
